import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { axiosInstance } from "../../../axios/axiosInstance";
import { useAuth } from "../../../Context/AuthContext";
import { useGlobalFunction } from "../../../Context/GlobalFunctionContext";
import { ProductGallerySkeleton } from "../../../Skeleton/productGallerySkeleton";
import { BreadcrumbSkeleton } from "../../../Skeleton/breadCrumbSkeleton";
import { HybridpageRightLoader } from "../../../Skeleton/hybridpageRightLoader";
import { setPageTitle } from "../../../utils/pageTitle";
import { ProductDetailsContainer } from "../productDetailsContainer";
import { HtmlFeatureBlogContainer } from "../htmlFeatureBlogContainer";
import { RelatedProductsContainer } from "../relatedProductsContainer";
import { FrequentlyBoughtTogether } from "../frequentlyBoughtTogether";
import { PopularProductContainer } from "../popularProductContainer";
import { HybridProductContainer } from "../family/hybridProductContainer";
import { HybridBreadCrumb } from "../../../Layout/Common/hybridBreadCrumb";
import { ProductOverlayContext } from "./productOverlayContext";
import { useProductPageOverlay } from "../productPageOverlay/productPageOverlayContext";
import { OVERLAY_BASE_Z_INDEX } from "../productPageOverlay/overlayNavigation";
import { useFlashLoaderLoader } from "../../../Context/FlashScreenContext";
import { useOverlayShell } from "./useOverlayShell";

const noop = () => {};

export const ProductOverlay = ({
  sku,
  fid,
  cachedProduct,
  familyProducts,
  breadCrumb,
  onClose,
  onSkuChange,
  leaving = false,
}) => {
  const [pageData, setPageData] = useState(null);
  const [stock, setStock] = useState(null);
  const [hybridData, setHybridData] = useState(null);
  const closeRef = useRef(null);
  const pageZIndex = useProductPageOverlay()?.zIndex;
  const { container, top, close } = useOverlayShell({ onClose: leaving ? noop : onClose, closeRef });
  const attachBack = useCallback((node) => {
    closeRef.current = node;
    if (node && document.activeElement === document.body) node.focus({ preventScroll: true });
  }, []);
  const backLink = (
    <div className="backAccount myAccount-back d-flex flex-start">
      <a
        href="#"
        ref={attachBack}
        role="button"
        aria-label="Back"
        onClick={(event) => {
          event.preventDefault();
          close();
        }}
      >
        <i className="fa-solid fa-chevron-left" /> Back
      </a>
    </div>
  );
  const { getStockStatus } = useGlobalFunction();
  const { userId, priceCode } = useAuth();
  const latest = useRef();
  const hideLoader = useFlashLoaderLoader()?.hideLoader;
  latest.current = { cachedProduct, fid, userId, priceCode, getStockStatus, hideLoader };

  useEffect(() => {
    let active = true;
    const { cachedProduct, fid, userId, priceCode, getStockStatus, hideLoader } = latest.current;
    setPageData(null);
    setStock(null);
    setHybridData(null);
    container.firstElementChild?.scrollTo(0, 0);

    axiosInstance
      .post("ProductNew/GetHybridData", {
        familyId: fid,
        userId: String(userId ?? 999),
        sku: sku,
        priceCode: String(priceCode ?? 3),
      })
      .then((response) => {
        if (active) setHybridData(response?.data || null);
      })
      .catch(() => {});

    const apply = (data) => {
      if (!active || !data || !data.familyDetails) return;
      const details = data.familyDetails;
      setPageData(data);
      hideLoader?.();
      setPageTitle(details.familyName);
      if (details.isFamily == false) {
        getStockStatus(details.productId ?? 0).then((response) => {
          if (active && response) setStock(response);
        });
      }
    };

    if (cachedProduct) {
      apply(cachedProduct);
    } else {
      axiosInstance
        .post("ProductNew/HybridInitLoad", {
          familyId: fid,
          userId: String(userId ?? 999),
          sku: sku,
          priceCode: String(priceCode ?? 3),
        })
        .then((response) => apply(response.data))
        .catch(() => {});
    }
    return () => {
      active = false;
    };
  }, [sku, container]);

  const overlayValue = useMemo(
    () => ({
      isOverlay: true,
      portalTarget: container,
      closeProduct: close,
      changeSku: onSkuChange,
      productUrl: `${window.location.origin}${window.location.pathname}?sku=${encodeURIComponent(sku)}`,
    }),
    [sku, container, close, onSkuChange],
  );

  const title = String(pageData?.familyDetails?.sku ?? sku).toUpperCase();

  return createPortal(
    <ProductOverlayContext.Provider value={overlayValue}>
      <div
        className={`product-overlay${leaving ? " product-overlay-leaving" : ""}`}
        style={{ top, zIndex: pageZIndex == null ? OVERLAY_BASE_Z_INDEX - 1 : pageZIndex + 1 }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {pageData ? (
          <Fragment>
            <HybridBreadCrumb data={breadCrumb} familyDetails={pageData.familyDetails} leading={backLink} />
            <ProductDetailsContainer
              params={pageData}
              stock={stock}
              setIshybrid={noop}
              hybridData={hybridData}
              familyProducts={familyProducts}
            />
            <HtmlFeatureBlogContainer details={pageData.familyDetails} position={"top"} />
            <HybridProductContainer familyDetails={pageData.familyDetails} ishybrid={false} />
            <HtmlFeatureBlogContainer details={pageData.familyDetails} position={"bottom"} />
            <RelatedProductsContainer params={pageData} />
            <PopularProductContainer params={pageData.familyDetails} />
            <FrequentlyBoughtTogether params={pageData.familyDetails} />
          </Fragment>
        ) : (
          <Fragment>
            <BreadcrumbSkeleton leading={backLink} />
            <div className="container-fluid container-lg mt-3">
              <div className="row">
                <div className="col-lg-12">
                  <div className="productsDetail-gallery">
                    <ProductGallerySkeleton />
                    <HybridpageRightLoader />
                  </div>
                </div>
              </div>
            </div>
          </Fragment>
        )}
      </div>
    </ProductOverlayContext.Provider>,
    container,
  );
};
